import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  LabelList,
  Pie,
  PieChart,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";

import { useQueryParams } from "@/hooks/use-query-params";

import {
  initialPieChart,
  initialPieChartPriority,
  JobOrderStatus,
  ViewPps,
} from "../utils/constants";

import {
  PieChartType,
  PieChartTypePriority,
} from "../../../types/status-overview.type";

import { JobOrderList } from "@/hooks/services/pps/handlers/job-orders-api";

import { Job, JobResponse } from "@/hooks/services/pps/types/job-orders.type";

import { useGetData } from "@/hooks/services/use-query-hooks";

import {
  barChartConfig,
  pieChartConfigPriority,
} from "../utils/status-overview.config";

import {
  parseFiltersFromQuery,
  parseSortsFromQuery,
  type DynamicFilter,
  type DynamicSort,
} from "@/components/search-filter-bar";

export const Route = createFileRoute(
  "/(planner-screens)/planner/status-overview",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { params } = useQueryParams();

  // Read filters and sorts from URL search params (managed by parent planner.tsx)
  const dynamicFilters: DynamicFilter[] = params.filters
    ? parseFiltersFromQuery(params.filters as string)
    : [];

  const dynamicSorts: DynamicSort[] = params.sorts
    ? parseSortsFromQuery(params.sorts as string)
    : [];

  const filterParams = {
    ...params,
    page: 1,
    pageSize: 100,
  };

  const [barChartData, setBarChartData] = useState<PieChartType[]>(
    initialPieChart || [],
  );
  const [pieChartPriorityData, setPieChartPriorityData] = useState<
    PieChartTypePriority[]
  >(initialPieChartPriority || []);

  const { data, isLoading } = useGetData<JobResponse>(
    JobOrderList({
      ...filterParams,
      viewId: ViewPps.overview.id,
      ...(dynamicSorts.length > 0 && {
        encodeQuery: { sorts: params.sorts },
      }),
      ...(dynamicFilters.length > 0 && {
        encodeQuery: { filters: params.filters },
      }),
    }),
  );

  useEffect(() => {
    const initPieChartPriority = () => {
      const updatedPieChartData = initialPieChartPriority.map((item) => {
        const tasksCount =
          data?.data.filter(({ priority }: any) => priority === item.priority)
            .length || 0;

        return {
          ...item,
          tasks: tasksCount,
        };
      });

      setPieChartPriorityData(updatedPieChartData);
    };

    const initPieChart = () => {
      const statuses = Object.values(JobOrderStatus);
      const newGroupedData: { [key: string]: Job[] } = {};

      statuses.forEach((status: string) => {
        const formattedKey = status.includes(" ")
          ? status.split(" ").join("_").toLowerCase()
          : status.toLowerCase();

        if (!newGroupedData[formattedKey]) {
          newGroupedData[formattedKey] = [];
        }

        const matchingItems = data?.data.filter(
          (item: any) => item.status?.statusName === status,
        );

        if (matchingItems?.length) {
          newGroupedData[formattedKey].push(...matchingItems);
        }
      });

      const groupedData = Object.entries(newGroupedData).map(
        ([groupKey, items]) => ({
          groupKey,
          items: items,
        }),
      );

      setBarChartData((prev) =>
        prev?.map((item) => {
          const group = groupedData.find(
            (groupItem) => groupItem.groupKey === item.id,
          );

          return {
            ...item,
            tasks: group?.items.length || 0,
          };
        }),
      );
    };

    initPieChart();
    initPieChartPriority();
  }, [isLoading, data]);

  return (
    <div className="aero:flex aero:flex-col aero:gap-4">
      {/* Subtitle */}
      <div className="aero:flex aero:items-center aero:gap-2">
        <span className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
        <h2 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
          Status Overview
        </h2>
      </div>

      <div className="aero:w-full aero:flex aero:justify-center">
        <Card className="aero:flex aero:flex-col aero:w-1/2 aero:mx-2">
          <CardHeader className="aero:!text-center">
            <CardTitle>Job Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={barChartConfig}>
              <BarChart
                accessibilityLayer
                data={barChartData}
                layout="vertical"
                margin={{
                  right: 16,
                }}
              >
                <CartesianGrid horizontal={false} stroke="#cccccc" />
                <YAxis
                  dataKey="title"
                  type="category"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  tickFormatter={(value) => value.slice(0, 3)}
                  hide
                />
                <XAxis dataKey="tasks" type="number" hide />
                <ChartTooltip
                  cursor={false}
                  content={<ChartTooltipContent indicator="line" />}
                />
                <Bar dataKey="tasks" layout="vertical" fill="red" radius={4}>
                  <LabelList
                    dataKey="title"
                    position="insideLeft"
                    offset={8}
                    className="aero:fill-[--color-label]"
                    fontSize={8}
                  />
                  <LabelList
                    formatter={(value: any) => {
                      if (!value) {
                        return;
                      }
                      return value;
                    }}
                    dataKey="tasks"
                    position="right"
                    offset={8}
                    className="aero:fill-foreground"
                    fontSize={12}
                  />
                </Bar>
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card className="aero:flex aero:flex-col aero:w-1/2 aero:mx-2">
          <CardHeader className="aero:!text-center">
            <CardTitle>Job Status Priority</CardTitle>
          </CardHeader>
          <CardContent className="aero:flex-1 aero:pb-0">
            <ChartContainer
              config={pieChartConfigPriority}
              className="aero:mx-auto aero:aspect-square aero:max-h-[250px] aero:pb-0 aero:[&_.recharts-pie-label-text]:fill-foreground"
            >
              <PieChart>
                <ChartTooltip
                  content={<ChartTooltipContent nameKey="name" hideLabel />}
                />
                <Pie
                  data={pieChartPriorityData}
                  dataKey="tasks"
                  nameKey="name"
                  labelLine={false}
                >
                  <LabelList
                    dataKey="name"
                    className="aero:fill-background"
                    stroke="none"
                    fontSize={12}
                    formatter={(value: keyof typeof pieChartConfigPriority) =>
                      pieChartConfigPriority[value]?.label
                    }
                  />
                </Pie>
              </PieChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
